import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useBalance } from '../context/BalanceContext';
import StartPopup from '../components/StartPopup';
import { motion } from 'framer-motion';
import { Wallet, TrendingUp, Zap, AlertTriangle, Clock } from 'lucide-react';
import facebookImage from '../assest/images/facebook.png';
import whatsappImage from '../assest/images/whatsapp.png';
import telegramImage from '../assest/images/paper-plane.png';
import bnbImage from '../assest/images/bnb.png';

const StatCard = ({ icon, label, value, color, isCurrency = true }) => (
  <motion.div
    className={`bg-white/10 backdrop-blur-xl border ${color} rounded-2xl p-6 flex items-center gap-4 shadow-lg hover:shadow-cyan-400/20 transition-all duration-300`}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03 }}
  >
    <div className="p-3 rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 shadow-md">
      {icon}
    </div>
    <div>
      <p className="text-slate-300 text-sm">{label}</p>
      <p className="text-white text-2xl font-bold tracking-tight">
        {isCurrency ? 'USDT ' : ''}
        {value}
      </p>
    </div>
  </motion.div>
);

const AppCard = ({ icon, name, amount }) => (
  <motion.div
    className="bg-white/10 backdrop-blur-xl border border-cyan-400/10 rounded-2xl p-4 flex items-center justify-between shadow-md hover:shadow-purple-400/20 transition-all duration-300"
    whileHover={{ scale: 1.02 }}
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
  >
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 bg-slate-800/60 rounded-full flex items-center justify-center p-2">
        {icon ? (
          <img src={icon} alt={name} className="w-full h-full object-contain" />
        ) : (
          <span className="text-yellow-400 font-bold text-xl">★</span>
        )}
      </div>
      <p className="text-white font-semibold">{name}</p>
    </div>
    <p className="text-cyan-300 font-mono">
      <span className="text-slate-400">USDT </span>
      {amount}
    </p>
  </motion.div>
);

const appListData = [
  { name: 'Facebook', icon: facebookImage, amount: '890.50' },
  { name: 'WhatsApp', icon: whatsappImage, amount: '570.80' },
  { name: 'Telegram', icon: telegramImage, amount: '954.20' },
  { name: 'Discord', icon: null, amount: '1540.90' },
  { name: 'Binance', icon: bnbImage, amount: '2190.30' },
];

// Countdown Timer Component
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [ukTime, setUkTime] = useState('');

  useEffect(() => {
    const calculateTimeLeft = () => {
      try {
        // Get current time in UK timezone
        const nowUk = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }));
        const ukTimeString = nowUk.toLocaleString('en-GB', {
          timeZone: 'Europe/London',
          hour12: false,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
        setUkTime(ukTimeString);

        // Calculate next reset time (next day at 00:00:00 UK time)
        const nextReset = new Date(nowUk);
        nextReset.setDate(nextReset.getDate() + 1);
        nextReset.setHours(0, 0, 0, 0);

        // Calculate the difference in milliseconds (all in UK time)
        const difference = nextReset.getTime() - nowUk.getTime();

        if (difference > 0) {
          const hours = Math.floor(difference / (1000 * 60 * 60));
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);

          setTimeLeft({ hours, minutes, seconds });
        } else {
          setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        }
      } catch (error) {
        console.error('Error calculating countdown:', error);
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex justify-between items-center gap-4">
      <div className="flex items-center gap-2 text-slate-300 text-sm">
        <Clock size={16} className="text-cyan-400" />
        <span>Next reset in:</span>
        <span className="font-mono font-bold text-cyan-400">
          {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
        </span>
      </div>
      <div className="text-slate-400 text-xs">
        UK Time: {ukTime}
      </div>
    </div>
  );
};

const Start = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [totalCompletedProfit, setTotalCompletedProfit] = useState(0);
  const { totalBalance, todayProfit, isBalanceNegative, fetchBalance, dailyRatingCount, setTotalBalance, fetchPendingCount } = useBalance();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [ratingId, setRatingId] = useState(null);

  useEffect(() => {
    if (token) fetchBalance();
  }, [token, fetchBalance]);

  useEffect(() => {
    const fetchTotalProfit = async () => {
      if (!token) return;
      try {
        const res = await axios.get('/api/profile/total-profit', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTotalCompletedProfit(res.data.totalProfit || 0);
      } catch (err) {
        setTotalCompletedProfit(0);
      }
    };
    fetchTotalProfit();
  }, [token, dailyRatingCount]);

  const allCompleted = dailyRatingCount >= 32;
  const progressPercentage = (dailyRatingCount / 32) * 100;

  const handleStartClick = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get('/api/apps/next', config);
      const app = res.data;
      if (!app || !app.amount || !app.profit) {
        alert('No app available for rating.');
        return;
      }
      const startData = {
        appId: app._id,
        requiredAmount: app.amount,
        appName: app.name,
        appImage: app.image,
        profitEarned: app.profit,
      };
      const startRes = await axios.post('/api/ratings/start', startData, config);
      setTotalBalance(startRes.data.newBalance);
      setSelectedApp(app);
      setRatingId(startRes.data.rating && startRes.data.rating._id ? startRes.data.rating._id : null);
      setIsPopupOpen(true);
      fetchPendingCount();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.message === 'No more apps to rate today.') {
        alert('You have completed all ratings for today!');
        return;
      }
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        alert(error.response.data.message);
      }
    }
  };

  const handlePopupClose = async (completed, appData) => {
    setIsPopupOpen(false);
    setSelectedApp(null);
    setRatingId(null);
    if (completed) {
      try {
        await fetchBalance();
      } catch (error) {
        // ignore
      }
    }
  };

  return (
    <>
    <Helmet>
      <title>Rate Apps & Earn USDT | RatingApp</title>
      <meta name="description" content="Start your daily app rating session and earn USDT. Complete up to 32 ratings per day and earn up to 10% profit per batch on RatingApp." />
    </Helmet>
    <div className="relative min-h-screen w-full overflow-hidden pb-24">

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-12">
        <motion.h1
          className="text-5xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-purple-500 text-center mb-4 drop-shadow-lg"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Web3 App Ratings
        </motion.h1>
        <p className="text-slate-400 text-center mb-10 text-lg font-medium">
          Earn by rating top Apps. Your daily progress, balance, and rewards at a glance.
        </p>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <StatCard
            icon={<Wallet size={28} className="text-white" />}
            label="Total Balance"
            value={totalBalance.toFixed(2)}
            color={totalBalance < 0 ? 'border-red-400/40' : 'border-cyan-400/40'}
          />
          <StatCard
            icon={<TrendingUp size={28} className="text-purple-400" />}
            label="Total Profit"
            value={totalCompletedProfit.toFixed(2)}
            color="border-purple-400/40"
          />
        </div>

        {/* Rating Progress and Action */}
        <motion.div
          className="bg-white/10 backdrop-blur-2xl border border-cyan-400/20 shadow-xl shadow-cyan-500/10 rounded-3xl p-8 space-y-8 transition-all duration-300 hover:border-purple-500/40 hover:shadow-purple-500/10"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="flex justify-between items-center text-white">
            <h2 className="font-bold text-2xl tracking-tight">Daily Ratings</h2>
            <p className="font-mono text-xl bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              {dailyRatingCount} / 32
            </p>
          </div>
          {/* Progress Bar */}
          <div className="w-full bg-slate-800/60 rounded-full h-3 shadow-inner">
            <motion.div
              className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 h-3 rounded-full shadow-lg shadow-cyan-400/30 animate-pulse"
              style={{ width: `${progressPercentage}%` }}
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>
          
          {/* Action Button */}
          {allCompleted ? (
            <button
              disabled
              className="w-full py-4 rounded-xl bg-slate-700 text-slate-400 font-bold text-lg cursor-not-allowed opacity-70"
            >
              All Tasks Completed
            </button>
          ) : totalBalance < 0 ? (
            <button
              onClick={() => navigate('/deposit')}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold text-lg shadow-lg hover:shadow-yellow-500/40 transition-all duration-300 hover:scale-[1.02] border-2 border-yellow-400/30"
            >
              <div className="flex items-center justify-center gap-2">
                <AlertTriangle size={22} />
                Top-up Balance to Continue
              </div>
            </button>
          ) : (
            <motion.button
              onClick={handleStartClick}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-500 text-white font-bold text-lg shadow-lg hover:shadow-purple-500/40 transition-all duration-300 border-2 border-cyan-400/30 hover:border-purple-400/40"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-center gap-2">
                <Zap size={22} />
                Start Next Rating
              </div>
            </motion.button>
          )}

          {/* Countdown Timer (moved below the button) */}
          <CountdownTimer />
        </motion.div>

        <StartPopup
          isOpen={isPopupOpen}
          onClose={handlePopupClose}
          appData={selectedApp}
          ratingId={ratingId}
        />

        <motion.div
          className="mt-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <h2 className="text-white text-3xl font-extrabold text-left mb-8 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-purple-500">
            Top Apps
          </h2>
          <div className="space-y-5">
            {appListData.map((app, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
              >
                <AppCard icon={app.icon} name={app.name} amount={app.amount} />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
      <style jsx="true" global="true">{`
        .web3-bg {
          background: #0d1117;
          min-height: 100vh;
        }
        .animate-pulse-slow {
          animation: pulse-slow 6s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
    </>
  );
};

export default Start;
